"use client";

import { useState, useCallback, useRef } from "react";
import { hotelSearchService } from "@/services/hotelServices/search";
import { getClientSessionHeaders } from "@/utils/clientSessionHeadersHelper";

const DEBOUNCE_MS = 500;

/**
 * Harita için otel listesi çeker. AbortController ile önceki istek iptal edilir.
 * "Bu bölgede ara" ile tetiklenir (otomatik bounds değişiminde değil).
 *
 * @param {Object} options
 * @param {string} options.checkinDate - YYYY.MM.DD
 * @param {string} options.checkoutDate - YYYY.MM.DD
 * @param {Array} options.occupancies - [{ paxList: [{ type, count, age }] }]
 * @param {Object} options.userFilterInput - buildUserFilterInput çıktısı (camelCase)
 * @param {Object} options.preferences - { language, currency, nationality, deviceType }
 * @param {string} [options.destination] - Arama lokasyonu (MapSearchFilters.Destination)
 * @returns {{ mapHotels: Array, loading: boolean, error: Error|null, fetchMapHotels: Function, abort: Function }}
 */
export function useHotelMapSearch({
  checkinDate,
  checkoutDate,
  occupancies = [],
  userFilterInput = null,
  preferences = {},
  destination,
} = {}) {
  const [mapHotels, setMapHotels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);
  const requestIdRef = useRef(0);

  const abort = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const fetchMapHotels = useCallback(
    async (boundingBox, options = {}) => {
      if (
        !boundingBox ||
        boundingBox.topLeft?.latitude == null ||
        boundingBox.topLeft?.longitude == null ||
        boundingBox.bottomRight?.latitude == null ||
        boundingBox.bottomRight?.longitude == null
      ) {
        setError(new Error("Geçerli harita sınırları gerekli"));
        return;
      }
      if (!checkinDate || !checkoutDate) {
        setError(new Error("Giriş ve çıkış tarihi gerekli"));
        return;
      }

      const isNewSearch = options.isNewSearch === true;

      abort();

      const controller = new AbortController();
      abortControllerRef.current = controller;
      const id = ++requestIdRef.current;

      setLoading(true);
      setError(null);

      try {
        const sessionHeaders = getClientSessionHeaders();
        const response = await hotelSearchService.getMapHotels(
          {
            boundingBox: {
              topLeft: {
                latitude: boundingBox.topLeft.latitude,
                longitude: boundingBox.topLeft.longitude,
              },
              bottomRight: {
                latitude: boundingBox.bottomRight.latitude,
                longitude: boundingBox.bottomRight.longitude,
              },
            },
            checkinDate,
            checkoutDate,
            occupancies,
            userFilterInput,
            preferences,
            isNewSearch,
            destination,
            signal: controller.signal,
          },
          sessionHeaders,
          true
        );

        if (id !== requestIdRef.current) return;

        const list = response?.MapHotels?.[0]?.MapHotelList ?? [];
        setMapHotels(list);
        setError(null);
      } catch (err) {
        if (err?.name === "AbortError" || err?.code === "ERR_CANCELED") return;
        if (id !== requestIdRef.current) return;
        const isNetworkError =
          err?.message === "Network Error" ||
          (err?.request && !err?.response);
        const friendlyError = new Error(
          isNetworkError
            ? "Harita otelleri şu an yüklenemiyor. Lütfen internet bağlantınızı kontrol edin veya daha sonra tekrar deneyin."
            : err?.response?.data?.Message || err?.message || "Bir hata oluştu."
        );
        setError(friendlyError);
        setMapHotels([]);
      } finally {
        if (id === requestIdRef.current) {
          setLoading(false);
          abortControllerRef.current = null;
        }
      }
    },
    [abort, checkinDate, checkoutDate, occupancies, userFilterInput, preferences, destination]
  );

  return { mapHotels, loading, error, fetchMapHotels, abort };
}
